import { algodClient } from '../App';
import {
  AssetType,
  Reputation,
  IPOOL,
  S3Asset,
  equalsIgnoreCase,
  getAssetImage,
} from './';
import { store } from '../redux/_store/configureStore';
import { ALGO_EXPLORER_ASSETS_URL } from '../constants/constants';
import BigNumber from 'bignumber.js';

const isHex = (value: string) => Boolean(String(value).match(/^0x[0-9a-f]+$/i))

export const buildAssetsFromPools = (pools: IPOOL[]): AssetType[] => {
  const walletAssets = store.getState().wallet.userAssets;
  const usdValues = store.getState().amm.usdValues;
  const assetsSet = new Set<string>();
  for (const pair of pools) {
    if (!walletAssets.some((a) => a.index === pair.aId)) {
      const assetA = {
        name: pair.aName,
        decimal: pair.aDecimals,
        index: pair.aId,
        amount: "0",
        unit_name: pair.aUnitName,
        img: getAssetImage(pair.aUnitName) || getAssetsImageLinkFromAlgoexplorer(pair.aId),
        reputation: Reputation.Unverified,
        usd_value: usdValues[pair.aId] || 0,
      };
      assetsSet.add(JSON.stringify(assetA));
    }
    if (!walletAssets.some((a) => a.index === pair.bId)) {
      const assetB = {
        name: pair.bName,
        decimal: pair.bDecimals,
        index: pair.bId,
        amount: "0",
        unit_name: pair.bUnitName,
        img: getAssetImage(pair.bUnitName) || getAssetsImageLinkFromAlgoexplorer(pair.bId),
        reputation: Reputation.Unverified,
        usd_value: usdValues[pair.bId] || 0,
      };
      assetsSet.add(JSON.stringify(assetB));
    }
  }
  return Array.from(assetsSet).map((asset) => JSON.parse(asset));
};

export const getAssetsImageLinkFromAlgoexplorer = (assetId: string) => {
  return `${ALGO_EXPLORER_ASSETS_URL}/asset-logo-${assetId}.image`;
}

export const compareWalletAssets = (assets: AssetType[]) => {
  const walletAssets: AssetType[] = store.getState().wallet.userAssets;

  const filteredAssets = assets.filter(
    (asset) =>
      !walletAssets.some((walletAsset) => equalsIgnoreCase(walletAsset.index, asset.index))
  );
  const sortedWalletAssets = walletAssets.sort((a, b) => BigNumber(b.amount).minus(a.amount).toNumber());
  const mergedAssets: AssetType[] = [...sortedWalletAssets, ...filteredAssets];

  return mergedAssets;
};

export const buildAsset = async (
  assetId: string,
  s3assets: S3Asset,
  amount?: number
): Promise<AssetType> => {
  const asset = s3assets[assetId];
  if (asset) {
    return {
      index: asset.id,
      amount: amount?.toString() || "0",
      name: asset.name,
      unit_name: asset.unitName,
      img: getAssetImage(asset.unitName) || getAssetsImageLinkFromAlgoexplorer(asset.id),
      decimal: asset.decimals,
      reputation: Reputation.Neutral,
      usd_value: 0,
    };
  } else {
    if (isHex(assetId)) {
      return {
        index: assetId as `0x${string}`,
        amount: amount?.toString() || "0",
        name: '',
        unit_name: '',
        img: '',
        decimal: 18,
        reputation: Reputation.Neutral,
        usd_value: 0,
      }
    }
    if (isNaN(Number(assetId))) {
      return {
        index: assetId as `0x${string}`,
        amount: amount?.toString() || "0",
        name: '',
        unit_name: '',
        img: '',
        decimal: 6,
        reputation: Reputation.Neutral,
        usd_value: 0,
      }
    }
    try {
      const detail = await algodClient.getAssetByID(Number(assetId)).do();
      return {
        index: detail.index,
        amount: amount?.toString() || "0",
        name: detail.params.name,
        unit_name: detail.params['unit-name'],
        img: getAssetImage(detail.params['unit-name']) || getAssetsImageLinkFromAlgoexplorer(detail.index),
        decimal: detail.params.decimals,
        reputation: Reputation.Neutral,
        usd_value: 0,
      };
    } catch (err) {
      return null;
    }
  }
};

export const buildAssetsForSwap = async (): Promise<AssetType[]> => {
  const walletAssets: AssetType[] = store.getState().wallet.userAssets;
  const topAssets: S3Asset = store.getState().amm.topAssets;
  const allAssets: S3Asset = store.getState().amm.assetsCache;

  const assetsForBuild = new Set(
    Object.keys(topAssets).map((id) => String(id))
  );

  Object.keys(allAssets).forEach((id) => {
    assetsForBuild.add(String(id));
  });

  const assets: AssetType[] = [];
  for (const id of assetsForBuild) {
    const asset = await buildAsset(id, allAssets);
    if (asset) assets.push(asset);
  }
  const sortedWalletAssets = walletAssets.sort((a, b) => BigNumber(b.amount).minus(a.amount).toNumber());
  return [...sortedWalletAssets, ...assets];
};
